import React, { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';

import { Separator } from '../../ui/separator';
import { useSelector } from 'react-redux';
import { useControlTower } from '../../../modules/control-tower/use-control-tower';
import { ControlTowerGraph } from './DayGraph';
import { useDispatch } from 'react-redux';
import { doGetCompanies } from '../../../modules/companies/stores/thunk';
import { AppDispatch, RootState } from '../../../redux/store';
import { selectAllCompanies } from '../../../modules/companies/stores/slice';
import { Skeleton } from '../../ui/skeleton';
import { selectAllCarriers } from '../../../modules/carriers/stores/slice';
import { doGetCarriers } from '../../../modules/carriers/stores/thunk';
import { CircleX, RotateCcw } from 'lucide-react';
import { Button } from '../../ui/button';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { ChildTooltip } from '../../Elements/ChildTooltip';
import { isBefore } from 'date-fns';
import { CutoffDialog } from './CutoffDialog';

export const ControlTower = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  const companies = useSelector((state: RootState) => selectAllCompanies(state));
  const carriers = useSelector((state: RootState) => selectAllCarriers(state));

  const { data, loading, query, setQuery, setUnits, units, refresh } = useControlTower();

  const [company, setCompany] = useState('');
  const [companyOpen, setCompanyOpen] = useState(false);
  const [carrier, setCarrier] = useState('');
  const [carrierOpen, setCarrierOpen] = useState(false);

  useEffect(() => {
    dispatch(doGetCompanies({ page: 1, take: 10000 }));
    dispatch(doGetCarriers({}));
  }, []);

  useEffect(() => {
    const query = {} as { carrierId?: string; companyId?: string };
    if (carrier) {
      query.carrierId = carrier;
    }
    if (company) {
      query.companyId = company;
    }
    setQuery(query);
  }, [carrier, company]);

  const resetFilters = () => {
    setCarrier('');
    setCompany('');
  };

  const onDrillClick = (v: { key: string; day: string; threshold: string }) => {
    const base = `?controlTower=true&key=${v.key}&day=${v.day}&threshold=${v.threshold}`;
    const companyString = company ? `&companyId=${company}` : '';
    const carrierString = carrier ? `&carrierId=${carrier}` : '';
    history.push(`/orders${base}${companyString}${carrierString}`);
  };
  const sortDays = (days: string[]) => {
    // past to future
    const order = days.sort((a, b) => (isBefore(new Date(a), new Date(b)) ? -1 : 1));
    // show
    // 1. today, 2. future
    // 3. furthest in past, 3. second furthest in the past
    return [order[2], order[3], order[0], order[1]];
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="flex justify-center px-4 py-10">
        <div className="w-full max-w-screen-xl bg-white">
          {/* Top Bar */}
          <div className="flex justify-between items-center">
            <div className="text-left">
              <h1 className="text-2xl font-semibold">Control Tower</h1>
              <p className="text-sm text-muted-foreground">Are you in control today?</p>
            </div>

            {/* Filters */}
            <div className="flex space-x-4">
              <Select value={units} onValueChange={(v: 'orders' | 'percentages') => setUnits(v)}>
                <SelectTrigger className="w-[180px] flex items-center">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {['percentages', 'orders'].map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select
                value={company}
                onValueChange={(newValue) => {
                  setCompany(newValue);
                }}
                open={companyOpen}
                onOpenChange={setCompanyOpen}
              >
                <SelectTrigger className="w-[180px] flex items-center">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  <Button
                    className="w-full px-2"
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompany('');
                      setCompanyOpen(false);
                    }}
                  >
                    <CircleX /> Clear company
                  </Button>
                  <SelectSeparator />
                  <SelectGroup>
                    {companies.map((c) => (
                      <SelectItem key={c.companyId} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select
                value={carrier}
                onValueChange={(v) => setCarrier(v)}
                open={carrierOpen}
                onOpenChange={setCarrierOpen}
              >
                <SelectTrigger className="w-[180px] flex items-center">
                  <SelectValue placeholder="Select a carrier" />
                </SelectTrigger>
                <SelectContent>
                  <Button
                    className="w-full px-2"
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCarrier('');
                      setCarrierOpen(false);
                    }}
                  >
                    <CircleX /> Clear carrier
                  </Button>
                  <SelectSeparator />
                  <SelectGroup>
                    {carriers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Button variant="secondary" onClick={resetFilters} disabled={!company && !carrier}>
                <CircleX />
                Reset filters
              </Button>
              <ChildTooltip title="Refresh data" asChild>
                <Button variant="secondary" size="icon" onClick={refresh} className="cursor-pointer">
                  <RotateCcw />
                </Button>
              </ChildTooltip>
              <CutoffDialog query={query} />
            </div>
          </div>

          {/* Divider Line */}
          <Separator className="mt-4" />

          {/* Cards Section */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            {loading && (
              <>
                <Skeleton className="w-full h-[33vw]" />
                <Skeleton className="w-full h-[33vw]" />
                <Skeleton className="w-full h-[33vw]" />
                <Skeleton className="w-full h-[33vw]" />
              </>
            )}
            {!loading && (
              <>
                {sortDays(Object.keys(data)).map((day) => (
                  <ControlTowerGraph
                    key={day}
                    day={day}
                    data={data?.[day]}
                    onDrillClick={onDrillClick}
                    xlabel={units}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
